import { Fragment } from 'react'
import { Listbox, Transition } from '@headlessui/react'
import { HiOutlineChevronDown, HiOutlineChevronUp } from 'react-icons/hi'
import DOMPurify from 'isomorphic-dompurify'

export interface IDropdownOption {
  key: string
  displayLabel: string
}

export function DropdownSelect<T extends IDropdownOption>({
  options,
  placeholder,
  selected = null,
  onChange,
  optionSelectionHelperText,
  validationHasFailed = false,
  validationErrorMessage,
  optionName,
  className,
  optionsListClassName = 'absolute',
}: {
  options: T[]
  placeholder?: string
  selected?: T
  onChange: (selectedValue: T) => void
  optionSelectionHelperText?: string
  validationHasFailed?: boolean
  validationErrorMessage?: string
  optionName?: string
  className?: string
  optionsListClassName?: string
}) {
  return (
    <Listbox value={selected} onChange={onChange}>
      {({ open }) => (
        <div className="relative my-6" role="listbox">
          <Listbox.Button
            className={`relative w-full px-6 py-4 text-left bg-white border-2 cursor-pointer ${
              validationHasFailed
                ? 'border-dashed border-core-light-error'
                : 'border-acai-5'
            } rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-opacity-75 focus-visible:ring-white focus-visible:ring-offset-pitaya focus-visible:ring-offset-2 selected ${className}`}
            aria-label={`${optionName?.toLowerCase()} ${
              selected?.displayLabel || placeholder
            }`}
          >
            <span
              className="block text-xl truncate text-acai"
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(
                  selected?.displayLabel || placeholder
                ),
              }}
            />

            <span className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none">
              {open ? (
                <HiOutlineChevronUp
                  className="w-8 h-8 text-acai"
                  aria-hidden="true"
                />
              ) : (
                <HiOutlineChevronDown
                  className="w-8 h-8 text-acai"
                  aria-hidden="true"
                />
              )}
            </span>
          </Listbox.Button>
          {validationHasFailed && validationErrorMessage && (
            <div className="px-3 py-1 text-base font-bold text-pitaya">
              {validationErrorMessage}
            </div>
          )}
          <Transition
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Listbox.Options
              className={`w-full px-6 py-3 mt-2 overflow-auto rounded-md shadow-lg bg-white max-h-80 z-1 ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm ${optionsListClassName}`}
            >
              {optionSelectionHelperText && (
                <div className="mb-2 text-lg text-black opacity-30">
                  {optionSelectionHelperText}
                </div>
              )}
              {options.map((option) => {
                const isOptionSelected = option.key === selected.key

                return (
                  <Listbox.Option
                    key={option.key}
                    className="flex items-center py-2 cursor-pointer select-none text-acai hover:text-pitaya"
                    value={option}
                  >
                    <span
                      className={`block truncate text-xl ${
                        isOptionSelected ? 'text-pitaya' : ''
                      }`}
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(option.displayLabel),
                      }}
                    />
                  </Listbox.Option>
                )
              })}
            </Listbox.Options>
          </Transition>
        </div>
      )}
    </Listbox>
  )
}
